/**
 * Vehicle dimension dropdown — controls the breakdown axis for any chart
 * that aggregates over vehicles. Per dashboard standards (PR 1 of 6),
 * every such chart should support: All vehicles | By make | By make &
 * model | By make / model / year. The wider catalogue (brand / region)
 * lights up as upstream data lands (see project_vehicle_dim_gap.md).
 *
 * Reads/writes the `?dim=` URL search param. Default is `all` (no
 * breakdown — single aggregate series). Most options are wired but
 * intentionally degrade to "no data yet" empty states downstream
 * until the vehicle catalogue is populated.
 */

import { useSearchParams } from 'react-router-dom';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

export type VehicleDim = 'all' | 'make' | 'make_model' | 'make_model_year';

const DIMS: VehicleDim[] = ['all', 'make', 'make_model', 'make_model_year'];

const DIM_LABELS: Record<VehicleDim, string> = {
  all: 'All vehicles',
  make: 'By make',
  make_model: 'By make & model',
  make_model_year: 'By make / model / year',
};

function parseDim(raw: string | null, fallback: VehicleDim = 'all'): VehicleDim {
  if (raw && (DIMS as string[]).includes(raw)) return raw as VehicleDim;
  return fallback;
}

export interface VehicleDimensionSelectProps {
  /** Dimension selected when the URL has no `dim` param. */
  defaultDim?: VehicleDim;
  /** Optional className applied to the trigger for layout overrides. */
  className?: string;
}

export function VehicleDimensionSelect({
  defaultDim = 'all',
  className,
}: VehicleDimensionSelectProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const current: VehicleDim = parseDim(searchParams.get('dim'), defaultDim);

  const handleChange = (next: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('dim', next);
    setSearchParams(params, { replace: false });
  };

  return (
    <Select value={current} onValueChange={handleChange}>
      <SelectTrigger
        className={cn('w-[220px]', className)}
        aria-label="Vehicle breakdown dimension"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {DIMS.map((dim) => (
          <SelectItem key={dim} value={dim}>
            {DIM_LABELS[dim]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
